/**
 * Sync layer.
 *
 * Two complementary sync providers:
 *
 *   - Yjs (CRDT (Conflict-free Replicated Data Type)) for live collaborative
 *     state — typing indicators, presence, active form drafts, in-call status.
 *     Lossy and ephemeral.
 *   - OrbitDB for the envelope log — durable, replicated, content-addressed.
 *
 * Both run over the same transport mesh.
 *
 * The interface is intentionally narrow so multiple backends can implement it:
 *
 *   - LocalSyncProvider     : in-memory, used in tests and single-node dev
 *   - OrbitDBSyncProvider   : full P2P (Peer-to-Peer) via Helia + OrbitDB
 *   - (future) MemoryReplicaProvider : multi-tab via BroadcastChannel for
 *     same-origin local replication
 */

import type { SignedEnvelope } from '../envelope/Envelope';
import type { Context } from '../identity';

export interface SyncStreamHandle {
  streamId: string;
  context: Context;
  /** Hash of stream head. */
  head: string;
  /** Lamport counter at head. */
  lamport: number;
}

export interface SyncProvider {
  /** Lifecycle: start the backing service (IPFS node, libp2p, etc). */
  start(): Promise<void>;

  /** Lifecycle: stop the backing service. */
  stop(): Promise<void>;

  /** Open or join a stream by id. */
  open(streamId: string, context: Context): Promise<SyncStreamHandle>;

  /** Append an envelope to the stream. */
  append(streamId: string, envelope: SignedEnvelope): Promise<void>;

  /** Subscribe to new envelopes from peers. */
  subscribe(streamId: string, onEnvelope: (e: SignedEnvelope) => void): () => void;

  /** Force a sync round with known peers. */
  pull(streamId: string): Promise<void>;

  /** Close stream, release subscription. */
  close(streamId: string): Promise<void>;

  /** Diagnostic: number of replication peers for a stream. */
  peerCount(streamId: string): Promise<number>;
}

// ─────────────────────────────────────────────────────────────────────────────
// LOCAL PROVIDER — in-memory, used for tests and single-node dev
// ─────────────────────────────────────────────────────────────────────────────

export class LocalSyncProvider implements SyncProvider {
  #streams = new Map<string, SignedEnvelope[]>();
  #subscribers = new Map<string, Set<(e: SignedEnvelope) => void>>();
  #started = false;

  async start(): Promise<void> {
    this.#started = true;
  }

  async stop(): Promise<void> {
    this.#started = false;
    this.#subscribers.clear();
  }

  async open(streamId: string, context: Context): Promise<SyncStreamHandle> {
    if (!this.#started) throw new Error('LocalSyncProvider not started');
    if (!this.#streams.has(streamId)) {
      this.#streams.set(streamId, []);
    }
    const stream = this.#streams.get(streamId)!;
    const head = stream[stream.length - 1];
    return {
      streamId,
      context,
      head: head?.hash ?? '',
      lamport: head?.header.lamport ?? 0,
    };
  }

  async append(streamId: string, envelope: SignedEnvelope): Promise<void> {
    const stream = this.#streams.get(streamId);
    if (!stream) throw new Error(`Stream ${streamId} not open`);
    stream.push(envelope);
    const subs = this.#subscribers.get(streamId);
    subs?.forEach((cb) => cb(envelope));
  }

  subscribe(streamId: string, onEnvelope: (e: SignedEnvelope) => void): () => void {
    // Pubsub semantics: subscribers receive FUTURE envelopes only. Late
    // joiners that need history fetch it via storage/iterator paths, not
    // by subscribing. Mirrors OrbitDB's `events.on('update')` contract.
    if (!this.#subscribers.has(streamId)) {
      this.#subscribers.set(streamId, new Set());
    }
    const subs = this.#subscribers.get(streamId)!;
    subs.add(onEnvelope);
    return () => subs.delete(onEnvelope);
  }

  async pull(_streamId: string): Promise<void> { /* no-op */ }

  async close(streamId: string): Promise<void> {
    this.#subscribers.delete(streamId);
  }

  async peerCount(_streamId: string): Promise<number> { return 0; }
}
